import { Component, EventEmitter, Injector, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormStatusEnum, FormType, Tag } from 'src/app/common/enumerations/enumerations';
import { pickFirstUnsigned, pickFirstUnsignedElseLast, pickLast, RsfMainSBaseComponent } from './rsf-main-s-base/rsf-main-s-base.component';
import { Kind, RSF, RSFAmendment, RSFButtonAction } from '../rsf';
import { RejectDialogComponent } from 'src/app/controls/reject-dialog/reject-dialog.component';
import { CancelDialogComponent } from 'src/app/controls/cancel-dialog/cancel-dialog.component';
import { RsfMainS6Component } from './rsf-main-s6/rsf-main-s6.component';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TesterToolsService } from 'src/app/services/environment/tester-tools.service';
import { Field } from '../rsf.service';
import { hasWorkDescriptionMentionChanges } from 'src/app/common/utils/rsf-minor-amendment-work-description.util';
import { RSFRefreshById } from '../store/rsf/rsf.action';

@Component({
  selector: 'rsf-main',
  templateUrl: './rsf-main.component.html',
  styleUrls: ['./rsf-main.component.scss']
})
export class RsfMainComponent extends RsfMainSBaseComponent implements OnChanges {
  @Output() buttonAction = new EventEmitter<RSFButtonAction>();

  @ViewChild(RsfMainS6Component) s6Component!: RsfMainS6Component;

  formValid?: boolean;
  /** Tracks whether the current amendment has persisted changes during this editing session. */
  amendmentChangesSaved = false;

  constructor(
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    protected override injector: Injector,
  ) {
    super(injector);
  }

  /**
   * Resets saved-change state when the user enters a different amendment session.
   * Sets amendmentChangesSaved to true if the amendment already has an ID (previously saved).
   * @param changes Angular input changes containing the current amendment selection.
   */
  override ngOnChanges(changes: SimpleChanges): void {
    // Reset the saved flag whenever a new amendment session begins.
    if (changes['rsfAmendment'] && changes['rsfAmendment'].currentValue?.id !== changes['rsfAmendment'].previousValue?.id) {
      // If the amendment has an ID, it means it was previously saved, so enable submit immediately
      this.amendmentChangesSaved = !!this.rsfAmendment?.id;
    }
  }

  /**
   * Whether the "Tester Tools" master toggle should be shown: only on non-production hosts AND for
   * System Administrators. The toggle itself is always visible to that audience (so they can turn
   * the tooling on); the individual tools (e.g. Section 1 auto-populate) additionally require the
   * toggle to be ON. Mirrors the SCF form's gating (see scf-v2-main + docs/scf-test-automation.md).
   */
  get showTesterToggle(): boolean {
    return !this.environmentService.isProduction() && this.isSA();
  }

  /** Two-way accessor for the shared, session-level Tester Tools master switch (bound to the toggle). */
  get testerToolsEnabled(): boolean {
    return this.testerToolsService.enabled;
  }
  setTesterToolsEnabled(value: boolean): void {
    this.testerToolsService.enabled = value;
  }

  //#region Tester tools: auto-advance (Phase 3)
  /** Guard so a second auto-advance can't start while one is in flight. */
  advancing = false;

  /**
   * Tester-only "Auto-advance" targets, in lifecycle order, filtered to what this form goes through:
   * Shielding Inspections only when the Shielding checkbox is set, PPS Testing only when the PPS
   * checkbox is set. Each carries its target FormStatusEnum and logical advance rank (the raw enum
   * ints are not monotonic). "Active" covers Active and Active/Hold Point; "Work Complete" covers
   * Work Complete and Work Complete / Follow-Up Needed (see advanceRank).
   */
  get advanceTargets(): RsfAdvanceTarget[] {
    const targets: RsfAdvanceTarget[] = [
      { label: 'Active', status: FormStatusEnum.Active, rank: 1 },
    ];
    if (this.rsf?.shielding) {
      targets.push({ label: 'Shielding Inspections', status: FormStatusEnum.ShieldingInspections, rank: 2 });
    }
    if (this.rsf?.pps) {
      targets.push({ label: 'PPS Testing', status: FormStatusEnum.PpsTesting, rank: 3 });
    }
    targets.push({ label: 'Work Complete', status: FormStatusEnum.WorkCompleted, rank: 4 });
    return targets;
  }

  /**
   * Whether the tester-only "Auto-advance" tooling shows: only on non-production hosts, for System
   * Administrators, with the Tester Tools toggle ON, and only once the form is on the linear advance
   * path (submitted or later). The server-side TestAdvance endpoint is the real guard.
   */
  get showAutoAdvance(): boolean {
    return (
      this.showTesterToggle &&
      this.testerToolsService.enabled &&
      this.advanceRank(this.rsf?.status) >= 0
    );
  }

  /**
   * Logical advancement rank for the RSF lifecycle, mirroring the backend RsfAdvanceRank so the
   * dropdown options grey out once the form is at/past them. Active and Active/Hold Point share a
   * rank; Work Complete and Work Complete / Follow-Up Needed share a rank. Returns -1 for statuses
   * off the linear advance path (Draft, Closed, canceled, amendment states).
   */
  advanceRank(status?: number): number {
    switch (status) {
      case FormStatusEnum.WorkApprovals:
      case FormStatusEnum.PendingActivation:
      case FormStatusEnum.PendingRequesterConcurrence:
      case FormStatusEnum.PendingSafetyControls:
        return 0;
      case FormStatusEnum.Active:
      case FormStatusEnum.HoldPoint:
        return 1;
      case FormStatusEnum.ShieldingInspections:
        return 2;
      case FormStatusEnum.PpsTesting:
      case FormStatusEnum.PpsTestingComplete:
        return 3;
      case FormStatusEnum.WorkCompleted:
      case FormStatusEnum.WorkCompleteFollowUps:
        return 4;
      case FormStatusEnum.Closed:
        return 5;
      default:
        return -1;
    }
  }

  /** An auto-advance option is available only while the form is below that target's rank. */
  canAdvanceTo(target: RsfAdvanceTarget): boolean {
    return this.advanceRank(this.rsf?.status) < target.rank;
  }

  /**
   * trackBy for the auto-advance menu. advanceTargets is a getter that returns a fresh array (with
   * new object literals) on every change-detection pass; without a stable trackBy the *ngFor would
   * destroy and recreate the menu items each cycle, which breaks the hover highlight and swallows
   * clicks. Keying by the target status keeps the DOM stable.
   */
  trackByAdvanceTarget = (_: number, target: RsfAdvanceTarget): number => target.status;

  /**
   * Tester tooling (non-production): asks the backend to apply every signature needed to advance the
   * form to the chosen status, in one request. The SignalR refresh that follows reloads the form for
   * all open clients; the returned resource is applied immediately as well.
   */
  autoAdvance(target: RsfAdvanceTarget): void {
    if (!this.rsf?.id || !this.showAutoAdvance || !this.canAdvanceTo(target) || this.advancing) return;
    this.advancing = true;
    this.service?.testAdvance(this.rsf.id, target.status).toPromise().then(
      (data) => {
        this.advancing = false;
        if (data) this.rsf = data;
      },
      (error) => {
        this.advancing = false;
        console.error(error);
        this.alert.defaultError();
      }
    );
  }
  //#endregion

  async buttonClicked({ action, data }: { action: RSFButtonAction, data?: any; }) {
    switch (action) {
      case RSFButtonAction.Cancel:
        this.save(FormStatusEnum.Canceled, data);
        break;

      case RSFButtonAction.SaveDraft:
      case RSFButtonAction.Unsubmit:
      case RSFButtonAction.Disapprove:
        this.save(FormStatusEnum.Draft, data);
        break;

      case RSFButtonAction.Submit:
        this.save(FormStatusEnum.WorkApprovals);
        break;

      case RSFButtonAction.SaveAmendment:
      case RSFButtonAction.UnsubmitAmendment:
      case RSFButtonAction.DisapproveAmendment:
        this.save(FormStatusEnum.AmendmentDrafting, data);
        break;

      case RSFButtonAction.SubmitAmendment:
        this.save(FormStatusEnum.AmendmentReviewApprovals);
        break;

      case RSFButtonAction.CancelAmendment:
        this.save(FormStatusEnum.Active);
        break;

      case RSFButtonAction.Discard:
        if (await this.canDeactivate()) {
          this.buttonAction.emit(RSFButtonAction.Discard);
        }
        break;

      default:
        this.alert.error("Action not Implemented!");
        break;
    }
  }

  //#region Save
  /**
   * Saves the current RSF form with the specified status.
   *
   * This method updates the RSF form's status, emits loading events, and handles workflow signatures
   * for submitting or un-submitting the form based on the status transition. It also displays a success alert,
   * marks the form as clean, and stops the loading indicator.
   *
   * @param status - The desired {@link FormStatusEnum} to set for the RSF form.
   *
   * @remarks
   * - If the form is in Amendment status, the status remains unchanged.
   * - If the status is set to PendingReview, the editingById field is cleared.
   * - If submitting, the relevant workflow signature is signed.
   * - If reverting from PendingReview to Draft, the unsubmit workflow signature is signed.
   *
   * @returns A Promise that resolves when the save operation and any required workflow actions are complete.
   */
  async save(status: FormStatusEnum, data?: any): Promise<void> {
    const previousStatus = this.rsf!.status;

    if (this.isFormDirty() && !!this.rsf?.status) {
      // Do not submit or clear pending edits when the amendment/form update is rejected.
      if (this.isAmendment()) {
        const updated = await this.updateAmendment();
        if (!updated) return;
        this.amendmentChangesSaved = true;
      } else {
        const updated = await this.update();
        if (!updated) return;
      }
      this.markSavedAndRefresh();
    }

    await this.processSignatures(previousStatus, status, data);

    // Optionally trigger notifications here
    // await this.createNotification();


  }

  /**
   * Marks the RSF as clean after a successful save and re-requests it from the store. The parent
   * skips store refreshes while edits are pending, so this guarantees the saved state is reloaded
   * even when the SignalR refresh arrived before the save response.
   */
  private markSavedAndRefresh(): void {
    this.setFormDirty(false);
    if (this.rsf?.id) this.store.dispatch(new RSFRefreshById(this.rsf.id));
  }

  /**
   * Returns a list of human-readable field names that have been changed in a minor
   * amendment but are not allowed to change (i.e. require a full amendment).
   *
   * Compares the current RSFAmendment against the original RSF for all
   * "full-amendment-trigger" fields.  Returns an empty array when the amendment is
   * not a minor amendment or when no restricted fields were touched.
   *
   * @returns Restricted fields or significant description changes that prevent submission.
   */
  getMinorAmendmentViolations(): string[] {
    if (!this.status?.isMinorAmendment || !this.rsf || !this.rsfAmendment) {
      return [];
    }

    const violations: string[] = [];

    const restrictedFields: { field: Field; label: string }[] = [
      { field: Field.Restrictions, label: 'Restrictions' },
      { field: Field.BeamlineLocations, label: 'Beamline Locations' },
      { field: Field.AcceleratorLocations, label: 'Accelerator Locations' },
      { field: Field.Requester, label: 'Requester' },
      { field: Field.ParentForms, label: 'Parent Forms' },
      { field: Field.ChildForms, label: 'Child Forms' },
      { field: Field.Shielding, label: 'Shielding' },
      { field: Field.ShieldingRemoveReplace, label: 'Shielding Remove/Replace' },
      { field: Field.ShieldingModification, label: 'Shielding Modification' },
      { field: Field.ShieldingModificationARC, label: 'Shielding Modification ARC' },
      { field: Field.ShieldingModificationBRC, label: 'Shielding Modification BRC' },
      { field: Field.PPS, label: 'PPS' },
      { field: Field.PPSMaintenance, label: 'PPS Maintenance' },
      { field: Field.PPSModification, label: 'PPS Modification' },
      { field: Field.FunctionalTest, label: 'Functional Tests' },
      { field: Field.RadMonitors, label: 'Rad Monitors' },
    ];

    for (const { field, label } of restrictedFields) {
      try {
        if (this.service?.checkDiff(field, this.rsf, this.rsfAmendment)) {
          violations.push(label);
        }
      } catch {
        // If checkDiff throws for an unsupported key, skip gracefully
      }
    }

    // Restrict only shielding mention changes inside Work Description for Minor Amendments.
    const descriptionViolation = this.getMinorAmendmentDescriptionViolation();
    if (descriptionViolation) {
      violations.push(`Work Description (${descriptionViolation})`);
    }

    return violations;
  }

  /**
   * Checks whether the amendment differs from the approved RSF in Section 1 or in its hold points.
   * Reads the live amendment model, so unsaved edits are included.
   * @returns True when the amendment has something to submit.
   */
  hasAmendmentChanges(): boolean {
    if (!this.rsf || !this.rsfAmendment) {
      return false;
    }

    // Section 1 fields that the amendment highlight logic already knows how to compare.
    const section1Fields: Field[] = [
      Field.ASCCForms, Field.ParentForms, Field.ChildForms, Field.RelatedLinks,
      Field.WorkDescription, Field.Requester, Field.InitDate, Field.EndDate, Field.Restrictions,
      Field.Beamline, Field.BeamlineLocations, Field.Accelerator, Field.AcceleratorLocations, Field.OtherSystems,
      Field.Shielding, Field.ShieldingRemoveReplace, Field.ShieldingModification, Field.ShieldingModificationARC, Field.ShieldingModificationBRC,
      Field.PPS, Field.PPSMaintenance, Field.PPSModification, Field.PPSModificationARC, Field.PPSModificationBRC,
      Field.PPSAcis, Field.TOMBIS, Field.PPSOther, Field.PPSOtherText,
      Field.PPSPLCSet, Field.PPSSoftware, Field.ETIAccess, Field.TOMBISPLCSet, Field.TOMBISSoftware, Field.TopOffInterlockSetPoint,
      Field.RadMonitors, Field.RadMonitorsMoving, Field.RadMonitorsDisconnecting, Field.RadMonitorsModifying, Field.RadMonitorsTripPoint,
      Field.RADSurveyRequired,
    ];

    const section1Changed = section1Fields.some(field => {
      try {
        return !!this.service?.checkDiff(field, this.rsf!, this.rsfAmendment!);
      } catch {
        // Unsupported comparison keys are ignored, matching the Minor Amendment guard.
        return false;
      }
    });

    return section1Changed || this.hasHoldPointChanges();
  }

  /**
   * Compares active amendment hold points with the approved RSF hold points using the same rules as the
   * Section 4 amendment highlight: count, number, description, and enabled approver roles/users.
   * @returns True when hold points were added, removed, or modified.
   */
  private hasHoldPointChanges(): boolean {
    const amended = (this.rsfAmendment?.rsfHoldPoints ?? []).filter(hp => hp.status == 1).sort((a, b) => a.number - b.number);
    const original = (this.rsf?.rsfHoldPoints ?? []).filter(hp => hp.status == 1).sort((a, b) => a.number - b.number);
    if (amended.length != original.length) return true;

    // Approver identity of a hold point: enabled task locals' role and user IDs, in order.
    const approvers = (hp: typeof amended[number]) => (hp.wfTaskLocals ?? [])
      .filter(tl => tl.wfTasks?.[0]?.enabled)
      .map(tl => `${tl.wfRoleLocals?.map(rl => rl.roleID).join('|')}/${tl.wfUserLocals?.map(ul => ul.userID).join('|')}`)
      .join(';');

    return amended.some((hp, i) => {
      const ohp = original[i];
      return hp.number != ohp.number || hp.description != ohp.description || approvers(hp) != approvers(ohp);
    });
  }

  /**
   * Checks whether a Work Description edit changed shielding mentions.
   * Plain text rewrites are allowed for Minor Amendments; changing mentioned shieldings requires a Full Amendment.
   *
   * @returns A user-facing reason when shielding references changed; otherwise `null`.
   */
  private getMinorAmendmentDescriptionViolation(): string | null {
    if (!this.rsf || !this.rsfAmendment) {
      return null;
    }

    return hasWorkDescriptionMentionChanges(
      this.rsf.description ?? '',
      this.rsfAmendment.description ?? ''
    ) ? 'shielding mentions changed' : null;
  }

  /**
   * Applies the workflow transition for the requested status. A first Save Draft on a New RSF
   * persists the form here and clears the dirty flag once the update succeeds.
   * @param previousStatus - Status the RSF had before the save started.
   * @param status - Target status requested by the clicked button.
   * @param data - Optional dialog payload (e.g. disapproval reason) forwarded to signing.
   */
  async processSignatures(previousStatus: FormStatusEnum, status: FormStatusEnum, data?: any) {
    // ----- Signature handling -----

    switch (status) {
      case FormStatusEnum.Draft:
        // Save Draft
        if (previousStatus == FormStatusEnum.New && this.rsf) {
          // Save/update the form
          this.rsf.status = FormStatusEnum.Draft;
          // Clear pending edits once persisted so the post-save store refresh can reload the RSF
          const updated = await this.update();
          if (updated) this.markSavedAndRefresh();
        }

        // Unsubmit or Disapprove
        // Auto-sign 'Unsubmit' task if reverting from 'PendingReview' to 'Draft'
        else if ((previousStatus == FormStatusEnum.WorkApprovals || data?.oicDisapprove) && this.rsf) {
          const submitSignature = this.getSignature(Tag.SUBMIT);
          if (submitSignature?.disapproveActionId) {
            await this.signTask(submitSignature, submitSignature.disapproveActionId, data);
          }
          else {
            this.alert.error('Signature Not Found!');
          }
        }
        else {
          this.alert.success('RSF Saved as Draft!');
        }
        break;

      case FormStatusEnum.WorkApprovals:
        // Auto-sign 'Submit' task if transitioning to 'WorkApprovals'
        if (this.rsf?.status === FormStatusEnum.Draft) {
          const submitSignature = this.getSignature(Tag.SUBMIT, pickFirstUnsignedElseLast);
          if (submitSignature?.approveActionId) {
            await this.signTask(submitSignature, submitSignature.approveActionId);
          }
        }
        break;

      case FormStatusEnum.AmendmentDrafting:

        if (previousStatus == FormStatusEnum.AmendmentDrafting) {
          this.alert.success('Amendment Save/Update');
        }
        else {
          const submitSignature = this.getSignature(Tag.SUBMIT_AMENDMENT);
          if (submitSignature?.disapproveActionId) {
            await this.signTask(submitSignature, submitSignature.disapproveActionId);
          }
        }
        break;

      case FormStatusEnum.AmendmentReviewApprovals: {
        // Guard: an amendment must change Section 1 or hold points before it can be submitted.
        if (!this.hasAmendmentChanges()) {
          this.alert.error('This Amendment has no changes in Section 1 or Hold Points. Make a change before submitting.');
          return;
        }

        // Guard: if this is a minor amendment, ensure no restricted fields were changed
        const violations = this.getMinorAmendmentViolations();
        if (violations.length) {
          this.alert.error(
            `Minor Amendment includes changes that may alter the approved work scope: ${violations.join(', ')}. ` +
            `Please correct the changes or use a Full Amendment.`
          );
          return;
        }

        const submitSignature = this.getSignature(Tag.SUBMIT_AMENDMENT, pickFirstUnsigned);
        if (submitSignature?.approveActionId) {
          await this.signTask(submitSignature, submitSignature.approveActionId);
        }
        break;
      }

      case FormStatusEnum.Active:
        if (previousStatus == FormStatusEnum.AmendmentDrafting) {
          const cancelDialog = this.dialog.open(RejectDialogComponent, {
            width: '600px',
            data: {
              warning: this.getMessage('RejectDialogWarning').description,
              title: this.getMessage("GenericActionConfirmation").description?.replace('{action}', '<b class="red">Cancel</b>').replace('{item}', `this <b class="blue">Amendment</b>`),
              placeholder: 'Please provide a reason for canceling this Amendment',
              icon: true,
            },
          });

          cancelDialog.afterClosed().subscribe(async (data) => {
            if (data.accept) {
              const submitSignature = this.getSignature(Tag.SUBMIT_AMENDMENT, pickFirstUnsignedElseLast);
              if (submitSignature?.cancelActionId) {
                await this.signTask(submitSignature, submitSignature.cancelActionId);
              }
            }
          });
        }

        if (previousStatus == FormStatusEnum.AmendmentReviewApprovals) {
          const submitSignature = this.getSignature(Tag.SUBMIT_AMENDMENT);
          if (submitSignature?.disapproveActionId) {
            await this.signTask(submitSignature, submitSignature.disapproveActionId);
          }
        }
        break;

      case FormStatusEnum.Canceled:
        const confirm = this.dialog.open(CancelDialogComponent, {
          width: "500px", data: {
            message: this.getMessage("SCF_Release_confirmation")
              ?.description, icon: "stop",
          },
        });
        confirm.afterClosed().subscribe(async (data) => {
          if (data.accept && this.rsf) {
            const submitSignature = this.getSignature(Tag.SUBMIT);
            if (submitSignature?.cancelActionId) {
              await this.signTask(submitSignature, submitSignature.cancelActionId, data);
              this.alert.warning('RSF Canceled!');
            }
          }
        });
        break;

      default:
        this.alert.error("Action not Implemented!");
        break;
    }
  }

  formValidChange(e: any) {
    setTimeout(() => {
      this.formValid = e;
    }, 100);

  }

  systemsAffectedChange({ rsfProxy, changes }: { rsfProxy: RSF | RSFAmendment, changes: string[] }) {
    this.rsf = rsfProxy.kind == Kind.RSF ? rsfProxy as RSF : this.rsf;
    this.rsfAmendment = rsfProxy.kind == Kind.RSFAmendment ? rsfProxy as RSFAmendment : this.rsfAmendment;

    if (changes.includes(SystemAffectedChanges.ShModARC)) {
      this.s6Component?.recomputeSignatures(SystemAffectedChanges.ShModARC);
    }
    else if (changes.includes(SystemAffectedChanges.ShModBRC)) {
      this.s6Component?.recomputeSignatures(SystemAffectedChanges.ShModBRC);
    }
  }

}

export enum SystemAffectedChanges {
  ShModARC = 'shieldingModificationARC',
  ShModBRC = 'shieldingModificationBRC',
}

/** A tester-only auto-advance target: a form status and its logical advance rank (see advanceRank). */
export interface RsfAdvanceTarget {
  label: string;
  status: FormStatusEnum;
  rank: number;
}
